import {
  Box,
  BoxProps,
  Button,
  Dialog,
  DialogWrapperProps,
  errorIcon,
  Loader,
  successIcon,
  useTheme,
} from '@ytl/common-web';
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as FailedIcon } from '../assets/PageNotFound2.svg';
import { ReactComponent as FinishIcon } from '../assets/SuccessGirl.svg';
import { selectCurrentUser } from '../contexts/currentUserSlicer';
import { useLanguage } from '../contexts/languageContext';
import { VerificationComponent } from '../modules/verificationComponent/VerificationComponent';
import { AppRoutes } from '../routes';
import { PasswordSetResult } from '../shared/@types/passwordSetResult';
import { WidgetEvent } from '../shared/@types/widgetEvents';
import { SetPassword } from '../shared/components/custom/set-password/SetPassword';
import { Banned } from '../shared/components/partials/verification/Banned';
import { missingSessionIdBoxConfig } from '../shared/data/boxConfig';
import { getConfig } from '../shared/data/config';
import { accountResource } from '../shared/resource/account.resource';
import { PasswordResource } from '../shared/resource/password.resource';
import { determineAppMode } from '../shared/utils/appMode';
import { classifyPasswordError } from '../shared/utils/classifyPasswordError';
import { onProcessPhase, onWidgetEvent } from '../shared/utils/emitData';
import { createUnsignedJWT } from '../shared/utils/jwtUtils';
import { usePassword } from '../shared/utils/usePassword';
import { SessionIdCheckState, useSessionIdCheck } from '../shared/utils/useSessionIdCheck';

enum ChangePasswordPhase {
  GetPassword = 'getPassword',
  PhoneVerification = 'phoneVerification',
  Finish = 'finish',
  Error = 'error',
  Banned = 'banned',
}

export const ChangePasswordPage = ({ isForgottenPasswordSetMode }: { isForgottenPasswordSetMode?: boolean }) => {
  const { translation } = useLanguage();
  const navigate = useNavigate();
  const { isSmallResolution, isWebView } = useTheme();
  const isB2BMode = determineAppMode() === 'b2b';
  const currentUser = useSelector(selectCurrentUser);
  const [phase, setPhase] = useState<ChangePasswordPhase>(ChangePasswordPhase.GetPassword);
  const [changePasswordResultState, setChangePasswordResultState] = useState<PasswordSetResult>(PasswordSetResult.NA);
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [passwordChangeEnabled, setPasswordChangeEnabled] = useState<boolean>(false);
  const [verificatorJwt, setVerificatorJwt] = useState<string | undefined>(undefined);
  const {
    password,
    setPassword,
    passwordAgain,
    setPasswordAgain,
    isLoading,
    setLoading,
    isContinueButtonDisabled,
    hasPasswordError,
    validatePasswords,
    getPasswordHints,
    passwordsAreNotMatching,
  } = usePassword();
  const { sessionIdCheckState, checkSession, sessionId } = useSessionIdCheck(setLoading);

  // JWT for verification component with phone verification enabled
  const initParamsJWT = useMemo(() => {
    const payload = {
      clientID: 'change-password-client',
      sessionID: 'change-password-session-' + Date.now(),
      withPassword: false,
      captchaNeeded: false,
      EmailVerification: false,
      TMSVerification: false,
      MSISDNVerification: currentUser?.category !== 'YTV',
      createDUP: false,
      salesMsisdn: '',
      salesCA: '',
      IPBanningEnabled: false,
      language: '',
      theme: '',
      showLoginButton: false,
    };
    return createUnsignedJWT(payload);
  }, [currentUser]);

  // Move to phone verification phase after password validation
  const continueClicked = useCallback(async () => {
    if (!validatePasswords()) {
      return;
    }
    let accountCategory = currentUser?.category;
    if (isForgottenPasswordSetMode) {
      const initSession = await PasswordResource.checkInitPasswordSessionId(sessionId as string);
      const getAccountResponse = await accountResource.getAccountByAccountId(initSession.accountId);
      setPhoneNumber(getAccountResponse[0].ctn || '');
      accountCategory = getAccountResponse[0].category;
    } else {
      setPhoneNumber(currentUser?.ctn || '');
    }
    if (accountCategory === 'YTV') {
      setPasswordChangeEnabled(true);
      return;
    }
    setPhase(ChangePasswordPhase.PhoneVerification);
  }, [validatePasswords, currentUser, isForgottenPasswordSetMode, sessionId]);
  // Listen to process phase changes from VerificationComponent
  useEffect(() => {
    const cleanup = onProcessPhase((processPhase: string) => {
      console.log('[ChangePassword] Received phase change:', processPhase);

      switch (processPhase) {
        case 'PhoneNumberConfirm':
          setPhase(ChangePasswordPhase.PhoneVerification);
          break;
        case 'Finish':
          // Phone verification completed, now save the password
          break;
      }
    });

    return cleanup;
  }, []);

  // Listen to VerificationComponent events
  useEffect(() => {
    return onWidgetEvent(async (data: any, token: string) => {
      console.log('[ChangePassword] WidgetEvent received:', data.eventName, data);
      switch (data.eventName) {
        case WidgetEvent.ProcessFinished:
          if (data.isSuccess) {
            setVerificatorJwt(token);
            setPasswordChangeEnabled(true);
          } else {
            setChangePasswordResultState(PasswordSetResult.Failed);
            if (data.eventValue?.banned) {
              setPhase(ChangePasswordPhase.Banned);
              return;
            }
            setPhase(ChangePasswordPhase.Error);
          }
          break;
      }
    });
  }, [password]);

  useEffect(() => {
    console.log('Password change enabled: ', passwordChangeEnabled);
    if (passwordChangeEnabled) {
      // Verification succeeded, now save the password
      const savePassword = async () => {
        try {
          setLoading(true);
          if (isForgottenPasswordSetMode) {
            await PasswordResource.forgotPassword(
              {
                newPassword: password,
                token: sessionId as string,
              },
              verificatorJwt,
              phoneNumber,
            );
          } else {
            console.log('Saving new password for accountId:', currentUser!.accountId);
            await PasswordResource.changePassword(
              {
                accountId: currentUser!.accountId,
                newPassword: password,
              },
              verificatorJwt,
              phoneNumber,
            );
          }
          console.log('Password change successful');
          setPhase(ChangePasswordPhase.Finish);
          setChangePasswordResultState(PasswordSetResult.Succeeded);
        } catch (err) {
          setPhase(ChangePasswordPhase.Error);
          setChangePasswordResultState(classifyPasswordError(err));
        } finally {
          setLoading(false);
        }
      };
      savePassword();
    }
  }, [
    passwordChangeEnabled,
    currentUser,
    isForgottenPasswordSetMode,
    password,
    sessionId,
    setLoading,
    verificatorJwt,
    phoneNumber,
  ]);

  const tryAgainClicked = useCallback(() => {
    setChangePasswordResultState(PasswordSetResult.NA);
    setPhase(ChangePasswordPhase.GetPassword);
    setPasswordChangeEnabled(false);
    setVerificatorJwt(undefined);
    setPassword('');
    setPasswordAgain('');
  }, [setPassword, setPasswordAgain]);

  // As soon as the Finish phase is reached, invalidate the session immediately.
  // logoutWithConfigUrl() clears the proxy cookie, deletes the browser session cookie,
  // and calls the configured OpenAM logout URL. It does NOT update Redux, so
  // isAuthenticated stays true in memory and the Finish screen keeps rendering.
  // If the user navigates away without pressing a button, the cookie is already
  // gone and they will be required to log in again.
  useEffect(() => {
    if (phase === ChangePasswordPhase.Finish) {
      accountResource.logoutWithConfigUrl(getConfig().openamLogoutUrl);
    }
  }, [phase]);

  const logoutAndRedirect = useCallback((url: string) => {
    window.location.href = url;
  }, []);

  // Dynamic header based on phase
  const header = useMemo(() => {
    if (phase === ChangePasswordPhase.PhoneVerification) {
      return translation.changeEmail.flow.header.confirmPhone;
    }
    if (phase === ChangePasswordPhase.Finish) {
      return '';
    }
    return '';
  }, [phase, translation]);

  // Dynamic text based on phase
  const text = useMemo(() => {
    if (phase === ChangePasswordPhase.PhoneVerification) {
      return translation.changeEmail.flow.text.confirmPhone;
    }
    if (phase === ChangePasswordPhase.Finish) {
      return '';
    }
    return '';
  }, [phase, translation]);

  const boxConfig: Omit<BoxProps, 'children'> = useMemo(() => {
    if (isForgottenPasswordSetMode) {
      // check session first
      if (sessionIdCheckState === SessionIdCheckState.NotChecked) {
        return {
          header: translation.changePassword.header,
          subHeader: translation.initialPasswordSet.subHeaderDuringLoading,
          action: (
            <Button
              disabled={true}
              aria-label={translation.accessibility.loading}
            >
              <Loader isLoading={isLoading} />
            </Button>
          ),
        };
      }
      if (sessionIdCheckState === SessionIdCheckState.Missing) {
        return missingSessionIdBoxConfig(translation);
      }
      if (sessionIdCheckState === SessionIdCheckState.CheckedAndInvalid) {
        // invalid session id state
        return {
          ...errorIcon,
          header: translation.changePassword.invalidSession.header,
          subHeader: translation.changePassword.invalidSession.subHeader,
          action: <Button onClick={() => AppRoutes.goToYettel()}>{translation.header.menu.backToYettel}</Button>,
        };
      }
      // other case means it's check and valid
    }

    // on mobile, we display the first state of change password
    if (changePasswordResultState === PasswordSetResult.NA || isSmallResolution) {
      return {
        header: translation.changePassword.header,
        subHeaderHTML: translation.changePassword.subHeader,
        action: (
          <Button
            disabled={isContinueButtonDisabled}
            onClick={continueClicked}
            aria-label={translation.changePassword.saveButton}
          >
            <Loader isLoading={isLoading} />
            {translation.changePassword.saveButton}
          </Button>
        ),
      };
    }

    if (changePasswordResultState === PasswordSetResult.Succeeded) {
      return {
        ...successIcon,
        header: translation.changePassword.success.header,
        subHeader: translation.changePassword.success.subHeader,
        action: (
          <Button onClick={() => (window.location.href = AppRoutes.homepage)}>
            {translation.changePassword.continueToAccount}
          </Button>
        ),
      };
    }

    return {
      ...errorIcon,
      headerHTML: translation.changePassword.error.header,
      subHeader: translation.changePassword.error.subHeader,
      action: <Button onClick={tryAgainClicked}>{translation.changePassword.tryAgain}</Button>,
    };
  }, [
    changePasswordResultState,
    continueClicked,
    isContinueButtonDisabled,
    isForgottenPasswordSetMode,
    isLoading,
    sessionIdCheckState,
    translation,
    tryAgainClicked,
    isSmallResolution,
  ]);

  const mobileDialogConfig = useMemo<null | DialogWrapperProps>(() => {
    if (
      ![PasswordSetResult.Succeeded, PasswordSetResult.Failed, PasswordSetResult.PasswordReuseError].includes(
        changePasswordResultState,
      )
    ) {
      return null;
    }

    if (changePasswordResultState === PasswordSetResult.Succeeded) {
      return {
        ...successIcon,
        title: translation.changePassword.success.header,
        onClose: () => {},
        content: (
          <>
            <div>{translation.changePassword.success.subHeader}</div>
            <div className="change-password-flow__back-to-app">
              {!isB2BMode && (
                <Button
                  variant={'contained'}
                  onClick={() => (window.location.href = AppRoutes.homepage)}
                >
                  {translation.changePassword.success.backToWeb}
                </Button>
              )}
              <Button
                variant={'contained'}
                onClick={() =>
                  isB2BMode
                    ? logoutAndRedirect(getConfig().backToB2bAppUrl)
                    : logoutAndRedirect(getConfig().backToAppUrl)
                }
              >
                {translation.changePassword.success.backToApp}
              </Button>
            </div>
          </>
        ),
      };
    }

    const isPasswordReuse = changePasswordResultState === PasswordSetResult.PasswordReuseError;
    const errorTranslation = isPasswordReuse
      ? translation.changePassword.errorPasswordReuse
      : translation.changePassword.error;
    return {
      ...errorIcon,
      titleHTML: errorTranslation.header,
      content: errorTranslation.subHeader,
      okLabel: translation.changePassword.tryAgain,
      onClose: tryAgainClicked,
      icon: <FailedIcon />,
    };
  }, [changePasswordResultState, logoutAndRedirect, translation, tryAgainClicked, isB2BMode]);

  useEffect(() => {
    if (isForgottenPasswordSetMode) {
      checkSession();
    } else {
      if (!currentUser) {
        navigate(AppRoutes.errorPage);
      }
    }
  }, [checkSession, currentUser, isForgottenPasswordSetMode, navigate]);

  useLayoutEffect(() => {
    document.documentElement.classList.add('change-password-page');
    if (isWebView) {
      document.documentElement.classList.add('ytl--app');
    }
    if (phase === ChangePasswordPhase.Banned) {
      document.documentElement.classList.remove('change-password-page');
    }
    return () => {
      document.documentElement.classList.remove('change-password-page');
      document.documentElement.classList.remove('ytl--app');
    };
  }, [isWebView, phase]);

  const errorBoxConfig = useMemo(() => {
    const isPasswordReuse = changePasswordResultState === PasswordSetResult.PasswordReuseError;
    const errorTranslation = isPasswordReuse
      ? translation.changePassword.errorPasswordReuse
      : translation.changePassword.error;
    return {
      ...errorIcon,
      headerHTML: errorTranslation.header,
      subHeader: errorTranslation.subHeader,
      action: <Button onClick={tryAgainClicked}>{translation.changePassword.tryAgain}</Button>,
    };
  }, [changePasswordResultState, translation, tryAgainClicked]);

  const finishBoxConfig = useMemo(() => {
    return {
      ...successIcon,
      header: translation.changePassword.success.header,
      subHeader: translation.changePassword.success.subHeader,
      action: (
        <Button
          variant="contained"
          onClick={() =>
            isWebView
              ? (window.location.href = getConfig().appLogoutUrl)
              : (window.location.href = getConfig().mainYettelUrl)
          }
        >
          {translation.changePassword.continueToAccount || translation.changePassword.continueToHome}
        </Button>
      ),
    };
  }, [translation, isWebView]);

  const backButtonAction = () => {
    if (phase === ChangePasswordPhase.GetPassword) {
      if (isWebView) {
        window.location.href = getConfig().closeWebViewUrl;
      } else {
        AppRoutes.goToYettelAccount();
      }
    } else {
      setPassword('');
      setPasswordAgain('');
      setPhase(ChangePasswordPhase.GetPassword);
    }
  };

  const BackButton = () => {
    return (
      <Button
        variant="text-undecorated"
        onClick={backButtonAction}
      >
        {translation.changePhone.buttonTextBack}
      </Button>
    );
  };

  const exitButtonAction = () => {
    if (isWebView) {
      window.location.href = getConfig().closeWebViewUrl;
    } else {
      AppRoutes.goToYettelAccount();
    }
  };

  // Finish screen
  if (phase === ChangePasswordPhase.Finish) {
    return (
      <div className={`change-password-flow${isWebView ? ' change-password-flow--webview' : ''}`}>
        {isWebView ? (
          <div className={`change-password-flow__finish${isWebView ? ' change-password-flow__finish--webview' : ''}`}>
            <FinishIcon />
            <h3>{translation.changePassword.success.header}</h3>
            <p dangerouslySetInnerHTML={{ __html: translation.changePassword.success.subHeader }}></p>
            <Button
              variant="contained"
              onClick={() => logoutAndRedirect(getConfig().appLogoutUrl)}
            >
              {translation.changePassword.continueToAccount || translation.changePassword.continueToHome}
            </Button>
          </div>
        ) : (
          <div className="change-password-flow">
            <Box {...finishBoxConfig} />
            {isSmallResolution && mobileDialogConfig && <Dialog {...mobileDialogConfig} />}
          </div>
        )}
      </div>
    );
  }

  // Error screen
  if (phase === ChangePasswordPhase.Error) {
    return (
      <div className={`change-password-flow${isWebView ? ' change-password-flow--webview' : ''}`}>
        {isWebView ? (
          <div className={`change-password-flow__finish${isWebView ? ' change-password-flow__finish--webview' : ''}`}>
            <FailedIcon />
            <h3 dangerouslySetInnerHTML={{ __html: errorBoxConfig.headerHTML as string }} />
            <p dangerouslySetInnerHTML={{ __html: errorBoxConfig.subHeader as string }}></p>
            <div className="change-password-flow__finish__action ">
              <Button
                variant="contained"
                onClick={() => (window.location.href = getConfig().appRedirectBackUrl)}
              >
                {translation.changePassword.tryAgain}
              </Button>
            </div>
          </div>
        ) : (
          <>
            <Box {...errorBoxConfig} />
            {isSmallResolution && mobileDialogConfig && <Dialog {...mobileDialogConfig} />}
          </>
        )}
      </div>
    );
  }

  // Banned screen
  if (phase === ChangePasswordPhase.Banned) {
    return <Banned buttonAction={exitButtonAction} />;
  }

  // Phone verification phase
  if (phase === ChangePasswordPhase.PhoneVerification) {
    return (
      <div className={`account-flow${isWebView ? ' account-flow--webview' : ''}`}>
        <div className={`account-flow__container${isWebView ? ' account-flow__container--webview' : ''}`}>
          <h3>{header}</h3>
          <p dangerouslySetInnerHTML={{ __html: text }}></p>
          <VerificationComponent
            initParamsJWT={initParamsJWT}
            showFinishScreen={false}
            skipGetPhoneNumber
            initialPhoneNumber={[phoneNumber]}
            disableBackButton
          />
          <BackButton />
        </div>
      </div>
    );
  }

  // GetPassword phase (default)
  return (
    <div className="change-password-flow">
      <Box
        icon={boxConfig.icon}
        header={boxConfig.header}
        headerHTML={boxConfig.headerHTML}
        subHeader={boxConfig.subHeader}
        subHeaderHTML={boxConfig.subHeaderHTML}
        action={boxConfig.action}
        subAction={!isForgottenPasswordSetMode ? <BackButton /> : undefined}
      >
        {(!isForgottenPasswordSetMode ||
          (isForgottenPasswordSetMode && sessionIdCheckState === SessionIdCheckState.CheckedAndValid)) &&
          changePasswordResultState === PasswordSetResult.NA && (
            <SetPassword
              password={password}
              passwordAgain={passwordAgain}
              passwordHints={getPasswordHints(password, passwordAgain)}
              hasPasswordError={hasPasswordError}
              passwordsAreNotMatching={passwordsAreNotMatching}
              setPassword={setPassword}
              setPasswordAgain={setPasswordAgain}
              isLoading={isLoading}
              onEnter={continueClicked}
            />
          )}
      </Box>

      {isSmallResolution && mobileDialogConfig && <Dialog {...mobileDialogConfig} />}
    </div>
  );
};
